// Service 23 — Expat relocation / VIP concierge (ที่พัก โรงเรียน ประกัน ธนาคาร ใบขับขี่ ทะเบียนราษฎร)
// อ้างอิงหลัก: พ.ร.บ.คนเข้าเมือง พ.ศ. 2522 มาตรา 37(4) และ 38 — สำนักงานตรวจคนเข้าเมือง (immigration.go.th),
// ระบบแจ้งที่พักออนไลน์ TM30 และแจ้งอยู่เกิน 90 วัน TM47 (tm30.immigration.go.th, extranet.immigration.go.th),
// กรมการปกครอง สำนักบริหารการทะเบียน — ทะเบียนบ้าน ท.ร.13 และบัตรประจำตัวคนซึ่งไม่มีสัญชาติไทย (bora.dopa.go.th),
// กรมการขนส่งทางบก — ใบอนุญาตขับรถสำหรับชาวต่างชาติ (dlt.go.th),
// ธนาคารแห่งประเทศไทย — หลักเกณฑ์การรับลูกค้าและ KYC (bot.or.th),
// สำนักงานคณะกรรมการส่งเสริมการศึกษาเอกชน (opec.go.th) และกระทรวงศึกษาธิการ (moe.go.th),
// สำนักงานคณะกรรมการกำกับและส่งเสริมการประกอบธุรกิจประกันภัย คปภ. (oic.or.th),
// ประกาศกระทรวงมหาดไทยเรื่องการตรวจลงตราประเภทคนอยู่ชั่วคราว Non-O-A (ประกันสุขภาพ) และ LTR โดย BOI (ltr.boi.go.th),
// กรมที่ดิน (dol.go.th) และ ป.พ.พ. ลักษณะเช่าทรัพย์ มาตรา 537–571
// Client-safe definitions only.

export type RelocationFaqCategory =
  | "housing-lease"
  | "tm30-reporting"
  | "banking-finance"
  | "health-insurance"
  | "schools-education"
  | "driving-vehicle"
  | "household-registration"
  | "vip-concierge";

export interface RelocationCategoryDef {
  slug: RelocationFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const RELOCATION_FAQ_CATEGORIES: RelocationCategoryDef[] = [
  {
    slug: "housing-lease",
    titleTh: "เช่าบ้านและคอนโดสำหรับชาวต่างชาติ",
    titleEn: "Renting a home or condo as a foreigner",
    descTh:
      "สัญญาเช่าตาม ป.พ.พ. มาตรา 537–571 การจดทะเบียนการเช่าเกิน 3 ปีที่สำนักงานที่ดิน เงินประกัน การตรวจสอบกรรมสิทธิ์ผู้ให้เช่า และเอกสารที่ต้องใช้ประกอบการขอวีซ่าและใบอนุญาตทำงาน",
    descEn:
      "Lease terms under sections 537–571 of the Civil and Commercial Code, registering leases longer than three years at the Land Office, deposits, verifying the landlord's title, and the lease documents visa and work-permit applications require.",
    matchTh: /เช่า|คอนโด|บ้าน|ผู้ให้เช่า|เงินประกัน|สำนักงานที่ดิน/,
    matchEn: /lease|rent|condo|landlord|deposit|land office/i,
  },
  {
    slug: "tm30-reporting",
    titleTh: "แจ้งที่พัก TM30 และรายงานตัว 90 วัน",
    titleEn: "TM30 address notification and 90-day reporting",
    descTh:
      "หน้าที่ของเจ้าบ้านตามมาตรา 38 พ.ร.บ.คนเข้าเมือง การแจ้ง TM30 ออนไลน์ การรายงานตัว 90 วันตามมาตรา 37(4) หนังสือรับรองถิ่นที่อยู่ และผลของการแจ้งล่าช้า",
    descEn:
      "The housemaster duty under section 38 of the Immigration Act, filing TM30 online, 90-day reporting under section 37(4), residence certificates, and what happens when a filing is late.",
    matchTh: /TM30|ตม\.30|90 วัน|รายงานตัว|เจ้าบ้าน|ถิ่นที่อยู่/,
    matchEn: /TM30|TM47|90-day|housemaster|residence certificate|report/i,
  },
  {
    slug: "banking-finance",
    titleTh: "เปิดบัญชีธนาคารและการโอนเงินเข้าประเทศ",
    titleEn: "Opening a bank account and moving funds in",
    descTh:
      "หลักเกณฑ์ KYC ของธนาคารแห่งประเทศไทย เอกสารที่ธนาคารมักขอจากชาวต่างชาติ หนังสือรับรองถิ่นที่อยู่จาก ตม. หรือสถานทูต แบบฟอร์มธุรกรรมเงินตราต่างประเทศ และหนังสือรับรองการโอนเงินเข้าเพื่อใช้ที่สำนักงานที่ดิน",
    descEn:
      "Bank of Thailand KYC rules, the documents banks usually ask foreigners for, residence certificates from Immigration or an embassy, foreign-exchange transaction forms, and the inward-remittance certificate the Land Office requires.",
    matchTh: /ธนาคาร|บัญชี|โอนเงิน|KYC|เงินตราต่างประเทศ|FET/,
    matchEn: /bank|account|remittance|KYC|foreign exchange|FET/i,
  },
  {
    slug: "health-insurance",
    titleTh: "ประกันสุขภาพสำหรับวีซ่าและการรักษาพยาบาล",
    titleEn: "Health insurance for visas and medical care",
    descTh:
      "เงื่อนไขประกันสุขภาพของวีซ่า Non-O-A และ LTR ทุนประกันขั้นต่ำที่ประกาศกำหนด การใช้บริษัทประกันที่กำกับโดย คปภ. เอกสารรับรองกรมธรรม์ที่ ตม. ยอมรับ และการรับรองเอกสารทางการแพทย์เพื่อเคลม",
    descEn:
      "Insurance conditions for Non-O-A and LTR visas, the published minimum cover, using insurers regulated by the OIC, policy certificates Immigration accepts, and certifying medical documents for claims abroad.",
    matchTh: /ประกัน|กรมธรรม์|คปภ|โรงพยาบาล|Non-O-A|LTR/,
    matchEn: /insur|policy|OIC|hospital|Non-O-A|LTR/i,
  },
  {
    slug: "schools-education",
    titleTh: "โรงเรียนและการย้ายเข้าเรียนของบุตร",
    titleEn: "Schools and enrolling your children",
    descTh:
      "การเลือกโรงเรียนที่ได้รับอนุญาตจากสำนักงานคณะกรรมการส่งเสริมการศึกษาเอกชน เอกสารย้ายโรงเรียนและใบรับรองผลการเรียนที่ต้องรับรองนิติกรณ์ วีซ่าผู้ติดตามและ Non-ED และการแปลเอกสารการศึกษา",
    descEn:
      "Choosing a school licensed by the Office of the Private Education Commission, transfer certificates and transcripts that need legalisation, dependent and Non-ED visas, and translating school records.",
    matchTh: /โรงเรียน|บุตร|นักเรียน|ผลการเรียน|Non-ED|การศึกษา/,
    matchEn: /school|child|student|transcript|Non-ED|education/i,
  },
  {
    slug: "driving-vehicle",
    titleTh: "ใบขับขี่ไทยและการซื้อ-โอนรถ",
    titleEn: "Thai driving licence and buying or transferring a vehicle",
    descTh:
      "การขอใบอนุญาตขับรถชนิดชั่วคราวและ 5 ปีที่กรมการขนส่งทางบก การเปลี่ยนใบขับขี่ต่างประเทศ ใบขับขี่สากลตามอนุสัญญาเจนีวา 1949 หนังสือรับรองถิ่นที่อยู่ และเอกสารโอนกรรมสิทธิ์รถ",
    descEn:
      "Applying for two-year temporary and five-year Thai licences at the Department of Land Transport, converting a foreign licence, International Driving Permits under the 1949 Geneva Convention, residence certificates, and vehicle transfer paperwork.",
    matchTh: /ใบขับขี่|ขนส่ง|รถ|เจนีวา|IDP/,
    matchEn: /driving licen|driver|vehicle|Geneva|IDP|transport/i,
  },
  {
    slug: "household-registration",
    titleTh: "ทะเบียนบ้านเหลือง ท.ร.13 และบัตรชมพู",
    titleEn: "Yellow house book (Tor Ror 13) and pink ID card",
    descTh:
      "การขอเพิ่มชื่อในทะเบียนบ้านสำหรับคนซึ่งไม่มีสัญชาติไทยที่สำนักทะเบียนอำเภอหรือเขต เอกสารที่ต้องแปลและรับรอง บัตรประจำตัวคนซึ่งไม่มีสัญชาติไทย และประโยชน์ในการทำธุรกรรม",
    descEn:
      "Adding a foreigner to the yellow house registration book at the district registrar, documents that must be translated and certified, the pink non-Thai ID card, and where they actually help in transactions.",
    matchTh: /ทะเบียนบ้าน|ท\.ร\.13|บัตรชมพู|สำนักทะเบียน|อำเภอ/,
    matchEn: /house (book|registration)|yellow book|pink|registrar|district office/i,
  },
  {
    slug: "vip-concierge",
    titleTh: "บริการดูแลแบบ VIP และพาไปติดต่อราชการ",
    titleEn: "VIP concierge and accompanied government visits",
    descTh:
      "บริการล่ามและทนายความพาไปติดต่อสำนักงานที่ดิน อำเภอ ตม. และธนาคาร การรับรองเอกสารนอกสถานที่ การจัดคิวเอกสารข้ามหน่วยงาน และการวางลำดับงานย้ายถิ่นฐานทั้งครอบครัว",
    descEn:
      "Interpreter and attorney accompaniment at the Land Office, district office, Immigration and banks, mobile document certification, sequencing filings across agencies, and planning a whole-family relocation.",
    matchTh: /VIP|ล่าม|พาไป|นอกสถานที่|ครอบครัว|ย้ายถิ่นฐาน/,
    matchEn: /VIP|concierge|interpreter|accompan|mobile|relocat/i,
  },
];

export function getRelocationCategoryDef(slug: string): RelocationCategoryDef | undefined {
  return RELOCATION_FAQ_CATEGORIES.find((c) => c.slug === slug);
}